import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowDownWideNarrow, MapPinned, ShieldCheck, SlidersHorizontal, X } from 'lucide-react';

export default function FilterBar({ filters, selectedFilters, onFilterChange, onClear }) {
  if (!filters) return null;

  const hasFilters = Object.entries(selectedFilters).some(([key, value]) => {
    if (key === 'sort_by' || key === 'sort_order') return false;
    return Boolean(value);
  });

  const activeFilters = [
    selectedFilters.country ? { label: selectedFilters.country } : null,
    selectedFilters.sector ? { label: selectedFilters.sector } : null,
    selectedFilters.geography ? { label: selectedFilters.geography } : null,
    selectedFilters.market_cap ? { label: selectedFilters.market_cap } : null,
    selectedFilters.validation_status ? { label: selectedFilters.validation_status.replace('_', ' ') } : null,
  ].filter(Boolean);

  return (
    <section className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.84)] p-4 shadow-[0_10px_30px_rgba(0,0,0,0.3)]" data-testid="dashboard-filter-bar">
      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-[#4ECDC4]" />
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-white/70">Analytics Controls</h2>
          </div>
          <p className="mt-1 text-sm text-white/45">Filter leaderboard coverage by Grand Prix market, sector, validation, and sorting preference.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <Badge key={filter.label} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-white/70">
              {filter.label}
            </Badge>
          ))}
          {hasFilters && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClear}
              data-testid="filters-clear-button"
              className="border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.08]"
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Reset filters
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
        <Select value={selectedFilters.country || 'all'} onValueChange={(value) => onFilterChange('country', value === 'all' ? '' : value)}>
          <SelectTrigger className="h-11 rounded-xl border-white/10 bg-white/[0.04] text-white/80" data-testid="filter-country-select">
            <MapPinned className="mr-2 h-4 w-4 text-[#4ECDC4]" />
            <SelectValue placeholder="GP country" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0D2238] text-white">
            <SelectItem value="all">All GP countries</SelectItem>
            {filters.countries.map((country) => (
              <SelectItem key={country} value={country}>
                {country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedFilters.sector || 'all'} onValueChange={(value) => onFilterChange('sector', value === 'all' ? '' : value)}>
          <SelectTrigger className="h-11 rounded-xl border-white/10 bg-white/[0.04] text-white/80" data-testid="filter-sector-select">
            <SelectValue placeholder="Sector" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0D2238] text-white">
            <SelectItem value="all">All sectors</SelectItem>
            {filters.sectors.map((sector) => (
              <SelectItem key={sector} value={sector}>
                {sector}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedFilters.geography || 'all'} onValueChange={(value) => onFilterChange('geography', value === 'all' ? '' : value)}>
          <SelectTrigger className="h-11 rounded-xl border-white/10 bg-white/[0.04] text-white/80" data-testid="filter-geography-select">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0D2238] text-white">
            <SelectItem value="all">All regions</SelectItem>
            {filters.geographies.map((geography) => (
              <SelectItem key={geography} value={geography}>
                {geography}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedFilters.market_cap || 'all'} onValueChange={(value) => onFilterChange('market_cap', value === 'all' ? '' : value)}>
          <SelectTrigger className="h-11 rounded-xl border-white/10 bg-white/[0.04] text-white/80" data-testid="filter-market-cap-select">
            <SelectValue placeholder="Market cap" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0D2238] text-white">
            <SelectItem value="all">All cap sizes</SelectItem>
            {filters.market_caps.map((marketCap) => (
              <SelectItem key={marketCap} value={marketCap}>
                {marketCap}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedFilters.validation_status || 'all'} onValueChange={(value) => onFilterChange('validation_status', value === 'all' ? '' : value)}>
          <SelectTrigger className="h-11 rounded-xl border-white/10 bg-white/[0.04] text-white/80" data-testid="filter-validation-select">
            <ShieldCheck className="mr-2 h-4 w-4 text-[#2A9D8F]" />
            <SelectValue placeholder="Validation" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0D2238] text-white">
            <SelectItem value="all">All validation states</SelectItem>
            {filters.validation_statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status.replace('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedFilters.sort_by} onValueChange={(value) => onFilterChange('sort_by', value)}>
          <SelectTrigger className="h-11 rounded-xl border-white/10 bg-white/[0.04] text-white/80" data-testid="sort-by-select">
            <ArrowDownWideNarrow className="mr-2 h-4 w-4 text-[#3498DB]" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0D2238] text-white">
            <SelectItem value="overall_score">Overall score</SelectItem>
            <SelectItem value="risk_score">Risk management</SelectItem>
            <SelectItem value="performance_score">Performance</SelectItem>
            <SelectItem value="impact_score">Impact</SelectItem>
            <SelectItem value="stewardship_score">Stewardship</SelectItem>
            <SelectItem value="improvement_pct">Improvement</SelectItem>
            <SelectItem value="country_rank">Country rank</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </section>
  );
}
